export const MUSCLE_GROUP_OPTIONS = [
  { value: 'chest', label: 'Chest', category: 'Upper Body' },
  { value: 'back', label: 'Back', category: 'Upper Body' },
  { value: 'lats', label: 'Lats', category: 'Upper Body' },
  { value: 'traps', label: 'Traps', category: 'Upper Body' },
  { value: 'shoulders', label: 'Shoulders', category: 'Upper Body' },
  { value: 'biceps', label: 'Biceps', category: 'Arms' },
  { value: 'triceps', label: 'Triceps', category: 'Arms' },
  { value: 'forearms', label: 'Forearms', category: 'Arms' },
  { value: 'quads', label: 'Quads', category: 'Lower Body' },
  { value: 'hamstrings', label: 'Hamstrings', category: 'Lower Body' },
  { value: 'glutes', label: 'Glutes', category: 'Lower Body' },
  { value: 'calves', label: 'Calves', category: 'Lower Body' },
  { value: 'adductors', label: 'Adductors', category: 'Lower Body' },
  { value: 'abductors', label: 'Abductors', category: 'Lower Body' },
  { value: 'core', label: 'Core', category: 'Core' },
  { value: 'abs', label: 'Abs', category: 'Core' },
  { value: 'obliques', label: 'Obliques', category: 'Core' },
  { value: 'lower_back', label: 'Lower Back', category: 'Core' },
  { value: 'full_body', label: 'Full Body', category: 'Full Body' },
] as const;

export type MuscleGroup = (typeof MUSCLE_GROUP_OPTIONS)[number]['value'];

export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> =
  MUSCLE_GROUP_OPTIONS.reduce(
    (labels, option) => ({ ...labels, [option.value]: option.label }),
    {} as Record<MuscleGroup, string>
  );

const MUSCLE_GROUP_VALUES = new Set<string>(
  MUSCLE_GROUP_OPTIONS.map((option) => option.value)
);

const MUSCLE_GROUP_ALIASES: Record<string, MuscleGroup> = {
  abdominals: 'abs',
  abdominal: 'abs',
  arms: 'biceps',
  back_upper: 'back',
  butt: 'glutes',
  delts: 'shoulders',
  deltoids: 'shoulders',
  hamstring: 'hamstrings',
  latisimus_dorsi: 'lats',
  latissimus_dorsi: 'lats',
  leg: 'quads',
  legs: 'quads',
  lowerback: 'lower_back',
  posterior_chain: 'hamstrings',
  quad: 'quads',
  quadriceps: 'quads',
  rear_delts: 'shoulders',
  traps_upper: 'traps',
  upper_back: 'back',
};

function toLookupKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function normalizeMuscleGroupValue(value: string): MuscleGroup | null {
  const key = toLookupKey(value);
  if (!key) return null;
  if (MUSCLE_GROUP_VALUES.has(key)) return key as MuscleGroup;
  return MUSCLE_GROUP_ALIASES[key] || null;
}

export function normalizeMuscleGroups(groups?: string[]) {
  const normalized: MuscleGroup[] = [];
  const seen = new Set<MuscleGroup>();

  for (const group of groups || []) {
    const value = normalizeMuscleGroupValue(group);
    if (value && !seen.has(value)) {
      normalized.push(value);
      seen.add(value);
    }
  }

  return normalized;
}

export function formatMuscleGroup(value: string) {
  const normalized = normalizeMuscleGroupValue(value);
  return normalized ? MUSCLE_GROUP_LABELS[normalized] : value;
}
